import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  FileText,
  AlertTriangle,
  Settings,
  MessageSquare,
  UserPlus,
  Loader2,
  Search,
  GraduationCap,
  ArrowRightLeft,
  TrendingDown,
  BookOpen,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useDemoMode } from "@/hooks/useDemoMode";
import { demoLeaders, demoSignals } from "@/data/demoData";
import { formatDistanceToNow } from "date-fns";

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenInviteDialog?: () => void;
}

interface LeaderResult {
  id: string;
  name: string;
  behaviorName?: string;
  phase?: string;
  lastActivity?: string;
}

interface TranscriptResult {
  id: string;
  title: string;
  leaderName: string;
  date: string;
  status: string;
}

interface SignalResult {
  id: string;
  type: string;
  leaderName: string;
  leaderId: string;
  summary: string;
  priority: "high" | "medium" | "low";
}

const getProfileDisplayName = (
  profile?: { full_name: string | null; email: string | null } | null,
) => profile?.full_name || profile?.email || "Unknown";

const PHASE_LABELS: Record<string, string> = {
  ACTIVE_PRACTICE: "Active Practice",
  CONSOLIDATION: "Consolidation",
  SYNTHESIS: "Synthesis",
  GRADUATED: "Graduated",
  PAUSED: "Paused",
};

export function CommandPalette({ isOpen, onClose, onOpenInviteDialog }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDemoMode } = useDemoMode();
  const [search, setSearch] = useState("");

  // Reset search when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSearch("");
    }
  }, [isOpen]);

  // Fetch leaders
  const { data: leaders = [], isLoading: leadersLoading } = useQuery({
    queryKey: ["command-palette-leaders", user?.id, isDemoMode],
    queryFn: async (): Promise<LeaderResult[]> => {
      if (isDemoMode) {
        return demoLeaders.map((l) => ({
          id: l.id,
          name: l.full_name,
          behaviorName: l.behaviorName,
          phase: l.phase,
          lastActivity: l.lastPracticeDate,
        }));
      }

      if (!user) return [];

      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select(`
          id,
          full_name,
          email,
          leader_behaviors (
            phase,
            last_practice_date,
            behaviors (name)
          )
        `)
        .eq("coach_id", user.id)
        .limit(50);
      if (profilesError) throw profilesError;

      return (profiles || []).map((p) => {
        const activeBehavior = p.leader_behaviors?.find(
          (lb: { phase?: string }) => lb.phase === "ACTIVE_PRACTICE" || lb.phase === "CONSOLIDATION"
        );
        return {
          id: p.id,
          name: p.full_name || p.email || "Unknown",
          behaviorName: activeBehavior?.behaviors?.name,
          phase: activeBehavior?.phase,
          lastActivity: activeBehavior?.last_practice_date,
        };
      });
    },
    enabled: isOpen && (!!user || isDemoMode),
    staleTime: 30000,
  });

  // Fetch transcripts
  const { data: transcripts = [], isLoading: transcriptsLoading } = useQuery({
    queryKey: ["command-palette-transcripts", user?.id, isDemoMode],
    queryFn: async (): Promise<TranscriptResult[]> => {
      if (isDemoMode) {
        return [
          { id: "t1", title: "1:1 with Alex Chen", leaderName: "Alex Chen", date: "2024-01-24", status: "completed" },
          { id: "t2", title: "Team Meeting", leaderName: "Jordan Smith", date: "2024-01-23", status: "pending" },
        ];
      }

      if (!user) return [];

      // First get leader IDs for this coach
      const { data: coachLeaders, error: coachLeadersError } = await supabase
        .from("profiles")
        .select("id")
        .eq("coach_id", user.id);
      if (coachLeadersError) throw coachLeadersError;

      const leaderIds = coachLeaders?.map((l) => l.id) || [];
      if (leaderIds.length === 0) return [];

      const { data: uploads, error: uploadsError } = await supabase
        .from("transcript_uploads")
        .select(`
          id,
          display_label,
          created_at,
          analysis_status,
          leader_id
        `)
        .in("leader_id", leaderIds)
        .order("created_at", { ascending: false })
        .limit(20);
      if (uploadsError) throw uploadsError;

      if (!uploads || uploads.length === 0) return [];

      // Fetch leader names
      const uploadLeaderIds = [...new Set(uploads.map((u) => u.leader_id))];
      const { data: leaderProfiles, error: leaderProfilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", uploadLeaderIds);
      if (leaderProfilesError) throw leaderProfilesError;

      const profileMap = new Map(leaderProfiles?.map((p) => [p.id, p]) || []);

      return uploads.map((t) => ({
        id: t.id,
        title: t.display_label || "Transcript",
        leaderName: getProfileDisplayName(profileMap.get(t.leader_id)),
        date: t.created_at,
        status: t.analysis_status || "pending",
      }));
    },
    enabled: isOpen && (!!user || isDemoMode),
    staleTime: 30000,
  });

  // Fetch signals
  const { data: signals = [], isLoading: signalsLoading } = useQuery({
    queryKey: ["command-palette-signals", user?.id, isDemoMode],
    queryFn: async (): Promise<SignalResult[]> => {
      if (isDemoMode) {
        return demoSignals.map((s) => ({
          id: s.id,
          type: s.signalType,
          leaderName: s.leaderName,
          leaderId: s.leaderId,
          summary: s.reason,
          priority: s.severity,
        }));
      }

      if (!user) return [];

      const { data: alerts, error: alertsError } = await supabase
        .from("coach_alerts")
        .select("id, alert_type, severity, title, leader_id")
        .eq("coach_id", user.id)
        .eq("dismissed", false)
        .order("created_at", { ascending: false })
        .limit(10);
      if (alertsError) throw alertsError;

      if (!alerts || alerts.length === 0) return [];

      const leaderIds = [...new Set(alerts.map((a) => a.leader_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", leaderIds);
      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

      return alerts.map((a) => ({
        id: a.id,
        type: a.alert_type,
        leaderName: getProfileDisplayName(profileMap.get(a.leader_id)),
        leaderId: a.leader_id,
        summary: a.title,
        priority: a.severity as "high" | "medium" | "low",
      }));
    },
    enabled: isOpen && (!!user || isDemoMode),
    staleTime: 30000,
  });

  // Filter results based on search
  const filteredLeaders = useMemo(() => {
    if (!search) return leaders.slice(0, 5);
    const query = search.toLowerCase();
    return leaders.filter((l) => l.name.toLowerCase().includes(query)).slice(0, 5);
  }, [leaders, search]);

  const filteredTranscripts = useMemo(() => {
    if (!search) return transcripts.slice(0, 3);
    const query = search.toLowerCase();
    return transcripts
      .filter((t) => t.title.toLowerCase().includes(query) || t.leaderName.toLowerCase().includes(query))
      .slice(0, 3);
  }, [transcripts, search]);

  const filteredSignals = useMemo(() => {
    if (!search) return signals.slice(0, 3);
    const query = search.toLowerCase();
    return signals
      .filter((s) => s.leaderName.toLowerCase().includes(query) || s.type.toLowerCase().includes(query))
      .slice(0, 3);
  }, [signals, search]);

  const isLoading = leadersLoading || transcriptsLoading || signalsLoading;

  const handleSelect = useCallback(
    (callback: () => void) => {
      onClose();
      callback();
    },
    [onClose]
  );

  const getSignalIcon = (type: string) => {
    switch (type) {
      case "graduation_check":
      case "graduation_review":
        return GraduationCap;
      case "sequence_check":
        return ArrowRightLeft;
      case "engagement_drop":
      case "engagement_trend":
        return TrendingDown;
      default:
        return AlertTriangle;
    }
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <CommandInput
        placeholder="Search leaders, transcripts, actions..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-[400px]">
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        <CommandEmpty>No results found.</CommandEmpty>

        {/* Leaders */}
        {filteredLeaders.length > 0 && (
          <CommandGroup heading="Leaders">
            {filteredLeaders.map((leader) => (
              <CommandItem
                key={leader.id}
                value={`leader-${leader.id}-${leader.name}`}
                onSelect={() => handleSelect(() => navigate(`/coach/leaders/${leader.id}`))}
                className="flex items-center gap-3 py-3"
              >
                <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{leader.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {leader.behaviorName || "No active behavior"}
                    {leader.phase && ` • ${PHASE_LABELS[leader.phase] || leader.phase}`}
                  </p>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Transcripts */}
        {filteredTranscripts.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Transcripts">
              {filteredTranscripts.map((transcript) => (
                <CommandItem
                  key={transcript.id}
                  value={`transcript-${transcript.id}-${transcript.title}`}
                  onSelect={() => handleSelect(() => navigate(`/coach/post-meeting/${transcript.id}`))}
                  className="flex items-center gap-3 py-3"
                >
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{transcript.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {transcript.leaderName} • {formatDistanceToNow(new Date(transcript.date), { addSuffix: true })}
                    </p>
                  </div>
                  <Badge
                    variant={transcript.status === "completed" ? "success" : "secondary"}
                    className="text-xs shrink-0"
                  >
                    {transcript.status}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Signals */}
        {filteredSignals.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Signals">
              {filteredSignals.map((signal) => {
                const SignalIcon = getSignalIcon(signal.type);
                return (
                  <CommandItem
                    key={signal.id}
                    value={`signal-${signal.id}-${signal.leaderName}`}
                    onSelect={() => handleSelect(() => navigate(`/coach/leaders/${signal.leaderId}`))}
                    className="flex items-center gap-3 py-3"
                  >
                    <SignalIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{signal.leaderName}</p>
                      <p className="text-xs text-muted-foreground truncate">{signal.summary}</p>
                    </div>
                    <Badge
                      variant={
                        signal.priority === "high"
                          ? "destructive"
                          : signal.priority === "medium"
                          ? "warning"
                          : "secondary"
                      }
                      className="text-xs shrink-0"
                    >
                      {signal.priority}
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {/* Quick Actions */}
        <CommandSeparator />
        <CommandGroup heading="Quick Actions">
          <CommandItem
            value="action-invite-new-leader"
            onSelect={() =>
              handleSelect(() => {
                if (onOpenInviteDialog) {
                  onOpenInviteDialog();
                } else {
                  navigate("/coach/leaders");
                }
              })
            }
            className="flex items-center gap-3 py-3"
          >
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            <span>Invite new leader</span>
          </CommandItem>
          <CommandItem
            value="action-view-messages"
            onSelect={() => handleSelect(() => navigate("/coach/messages"))}
            className="flex items-center gap-3 py-3"
          >
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <span>View messages</span>
          </CommandItem>
          <CommandItem
            value="action-coaching-system"
            onSelect={() => handleSelect(() => navigate("/coach/identity"))}
            className="flex items-center gap-3 py-3"
          >
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <span>My Coaching System</span>
          </CommandItem>
          <CommandItem
            value="action-settings"
            onSelect={() => handleSelect(() => navigate("/coach/settings"))}
            className="flex items-center gap-3 py-3"
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span>Settings</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
